import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axiosInstance from "../../utils/api";
import ExcelJS from "exceljs";
import toast from "react-hot-toast";
import { FiUpload, FiFile, FiCheckCircle, FiActivity, FiDownload, FiLink, FiCheck } from "react-icons/fi";
import { CiMap } from "react-icons/ci";
import { handleDownloadTemplate } from "../../utils/downloadRetroplanTemplate";
import { getSessionUser } from "../../utils/session";

const RetroPlan = () => {
  const { id: eventId } = useParams();
  const [activities, setActivities] = useState([]);
  const [stands, setStands] = useState([]);
  const [search, setSearch] = useState("");
  const [hallFilter, setHallFilter] = useState("all");
  const [tentFilter, setTentFilter] = useState("all");
  const [retroFilter, setRetroFilter] = useState("all");
  const [uploading, setUploading] = useState(false);
  const [hasRetro, setHasRetro] = useState(false);
  const sessionUser = getSessionUser();
  const isSubAdmin = sessionUser.role === "sub_admin";
  const [linkCopied, setLinkCopied] = useState(false);


  const fetchEventActivities = async () => {
    try {
      const res = await axiosInstance.get(`/activity/event/${eventId}`);
      setActivities(res.data.activities || []);
    } catch {
      toast.error("Failed to load activities");
    }
  };

  const fetchEventStands = async () => {
    try {
      const res = await axiosInstance.get(`/stand/${eventId}`);
      setStands(res.data.stands || []);
    } catch {
      toast.error("Failed to load stands");
    }
  };


  const fetchEvent = async () => {
    try {
      const res = await axiosInstance.get(`/event/${eventId}`);
      setHasRetro(res.data.event.hasRetroPlan);

    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchEventActivities();
    fetchEventStands();
    fetchEvent();
  }, [eventId]);

  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const filteredStands = stands.filter((stand) => {
    const matchesSearch =
      stand.exhibitorName?.toLowerCase().includes(search.toLowerCase()) ||
      stand.standNumber?.toString().includes(search);

    const matchesHall =
      hallFilter === "all" || stand.hall?.toString() === hallFilter;

    const matchesTent =
      tentFilter === "all" || stand.tentNumber?.toString() === tentFilter;

    const matchesRetro =
      retroFilter === "all" ||
      (retroFilter === "yes" && stand.hasRetroPlan) ||
      (retroFilter === "no" && !stand.hasRetroPlan);

    return matchesSearch && matchesHall && matchesTent && matchesRetro;
  });

  const findExhibitorName = (worksheet) => {
    for (let r = 1; r <= 20; r++) {
      const row = worksheet.getRow(r);

      for (let c = 1; c <= 35; c++) {
        const cell = row.getCell(c);
        if (!cell.value) continue;

        const cellValue = String(cell.value).trim();
        const lowerValue = cellValue.toLowerCase();

        const isLabel =
          (lowerValue.includes("exhibitor") && lowerValue.includes("company")) ||
          lowerValue === "exhibitor company name:" ||
          lowerValue === "exhibitor company name" ||
          lowerValue === "exhibitor name:" ||
          lowerValue === "exhibitor name";

        if (!isLabel) continue;

        if (cellValue.includes(":")) {
          const parts = cellValue.split(":");
          if (parts[1] && parts[1].trim()) {
            const name = parts[1].trim();
            console.log(`Found exhibitor name (same cell R${r}C${c}):`, name);
            return name;
          }
        }

        for (let offset = 1; offset <= 15; offset++) {
          const valueCell = row.getCell(c + offset);
          if (!valueCell.value) continue;
          const name = String(valueCell.value).trim();
          const nameLower = name.toLowerCase();
          if (
            name.length > 0 &&
            !nameLower.includes("exhibitor") &&
            !nameLower.includes("company name") &&
            !nameLower.endsWith(":")
          ) {
            console.log(`Found exhibitor name (same row R${r}C${c + offset}):`, name);
            return name;
          }
        }

        for (let rowOffset = 1; rowOffset <= 5; rowOffset++) {
          const belowRow = worksheet.getRow(r + rowOffset);
          const valueCell = belowRow.getCell(c);
          if (!valueCell.value) continue;
          const name = String(valueCell.value).trim();
          const nameLower = name.toLowerCase();
          if (
            name.length > 0 &&
            !nameLower.includes("exhibitor") &&
            !nameLower.includes("company name") &&
            !nameLower.endsWith(":")
          ) {
            console.log(`Found exhibitor name (below label R${r + rowOffset}C${c}):`, name);
            return name;
          }
        }
      }
    }

    console.log('No exhibitor name found');
    return null;
  };

  const findStandNumber = (worksheet) => {
    for (let r = 1; r <= 20; r++) {
      const row = worksheet.getRow(r);
      for (let c = 1; c <= 35; c++) {
        const cell = row.getCell(c);
        if (!cell.value) continue;

        const cellValue = String(cell.value).trim();
        const lowerValue = cellValue.toLowerCase();

        const isLabel =
          lowerValue === "stand number:" ||
          lowerValue === "stand number" ||
          lowerValue === "stand no:" ||
          lowerValue === "stand no" ||
          (lowerValue.includes("stand") && lowerValue.includes("number"));

        if (!isLabel) continue;

        // Case 1: value embedded in same cell after colon e.g. "Stand Number: A12"
        if (cellValue.includes(":")) {
          const afterColon = cellValue.split(":").slice(1).join(":").trim();
          if (afterColon.length > 0) return afterColon;
        }

        // Case 2: search right across more columns (handles merged cells)
        for (let offset = 1; offset <= 15; offset++) {
          const valueCell = row.getCell(c + offset);
          // Also check the master cell of merged ranges
          const val = valueCell.value != null
            ? String(valueCell.value).trim()
            : null;
          if (val && val.length > 0 && !val.toLowerCase().includes("stand")) {
            return val;
          }
        }

        // Case 3: search below
        for (let rowOffset = 1; rowOffset <= 5; rowOffset++) {
          const valueCell = worksheet.getRow(r + rowOffset).getCell(c);
          if (!valueCell.value) continue;
          const val = String(valueCell.value).trim();
          if (val.length > 0 && !val.toLowerCase().includes("stand")) {
            return val;
          }
        }
      }
    }
    return null;
  };

  const findHeaderRow = () => {
    return 7;
  };

  const isStopColumn = (value) => {
    if (!value) return false;
    const lower = String(value).trim().toLowerCase();
    return (
      lower.includes("no more stand") ||
      lower.includes("no work") ||
      lower.includes("no more work") ||
      lower.includes("stand build complete") ||
      lower.includes("build complete") ||
      lower.includes("no build") ||
      lower.includes("show open") ||
      lower.includes("show day") ||
      lower.includes("open day")
    );
  };

  const extractDates = (worksheet, headerRowNum) => {
    const dates = {};
    const headerRow = worksheet.getRow(headerRowNum);

    for (let c = 3; c <= 26; c++) {
      const headerCell = headerRow.getCell(c);

      let stopFound = false;
      for (let r = 1; r <= headerRowNum + 3; r++) {
        const scanCell = worksheet.getRow(r).getCell(c);
        if (scanCell.value && isStopColumn(scanCell.value)) {
          console.log(`Stop column found at R${r}C${c} — value: "${scanCell.value}"`);
          stopFound = true;
          break;
        }
      }

      if (stopFound) break;

      if (!headerCell.value) continue;

      let dateObj = null;
      const cellValue = headerCell.value;

      if (cellValue instanceof Date) {
        dateObj = cellValue;
      } else if (typeof cellValue === "number") {
        const excelEpoch = new Date(1899, 11, 30);
        dateObj = new Date(excelEpoch.getTime() + cellValue * 86400000);
      } else if (typeof cellValue === "string") {
        const match = cellValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (match) {
          const day = parseInt(match[1], 10);
          const month = parseInt(match[2], 10) - 1;
          const year = parseInt(match[3], 10);
          dateObj = new Date(year, month, day);
        }
      }

      if (dateObj && !isNaN(dateObj.getTime())) {
        dates[c] = dateObj;
      }
    }

    return dates;
  };

  const findActivityStartRow = () => {
    return 8;
  };

  const isFilledCell = (cell) => {

    if (cell.value !== null && cell.value !== undefined) {
      const strVal = String(cell.value).trim();
      if (strVal.length > 0) return true;
    }

    if (cell.fill && cell.fill.type === "pattern" && cell.fill.pattern !== "none") {
      const fgColor = cell.fill.fgColor;
      if (fgColor) {
        if (fgColor.argb || fgColor.rgb) {
          const colorHex = (fgColor.argb || fgColor.rgb || "").replace(/^FF/i, "").toLowerCase();
          if (colorHex && colorHex !== "ffffff") {
            const r = parseInt(colorHex.substr(0, 2), 16);
            const g = parseInt(colorHex.substr(2, 2), 16);
            const b = parseInt(colorHex.substr(4, 2), 16);

            const isBlue = b > 150 && b > r + 40 && b > g + 40;
            if (!isBlue) return true;
          }
        }
        if (fgColor.theme !== undefined && fgColor.theme !== 0) return true;
      }
    }

    if (cell.value === true) return true;

    return false;
  };

  const handleExcelUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    try {
      setUploading(true);
      const updates = [];

      for (const file of files) {
        const buffer = await file.arrayBuffer();
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(buffer);

        const worksheet = workbook.worksheets[0];
        if (!worksheet) continue;

        let standNumber = findStandNumber(worksheet);
        if (standNumber) {
          const lower = standNumber.toLowerCase();
          if (
            lower.includes("stand number") ||
            lower.includes("stand no") ||
            lower === ":"
          ) {
            console.warn("Stand number extraction returned a label, skipping:", standNumber);
            standNumber = null;
          }
        }
        const exhibitorName = findExhibitorName(worksheet); // keep for display/logging only
        console.log('Stand number extracted:', standNumber);
        if (!standNumber) {
          console.log('Skipping file - no stand number found');
          continue;
        }

        const headerRowNum = findHeaderRow();
        const dates = extractDates(worksheet, headerRowNum);
        console.log('Extracted dates for', exhibitorName, ':', dates);
        console.log('Number of dates found:', Object.keys(dates).length);
        if (Object.keys(dates).length === 0) {
          continue;
        }

        const activityStartRow = findActivityStartRow();
        const activityMap = new Map();

        const allDates = Object.values(dates).filter(Boolean);
        if (allDates.length === 0) continue;

        allDates.sort((a, b) => a - b);
        const eventStartDate = allDates[0];
        const eventEndDate = allDates[allDates.length - 1];

        for (let r = activityStartRow; r <= Math.min(activityStartRow + 30, worksheet.rowCount); r++) {
          const row = worksheet.getRow(r);
          const activityCell = row.getCell(2);

          if (!activityCell.value) continue;

          const activityName = String(activityCell.value).trim();
          if (!activityName) continue;

          let markedDates = [];

          for (const col in dates) {
            const cell = row.getCell(Number(col));
            if (isFilledCell(cell)) {
              markedDates.push(dates[col]);
            }
          }

          if (markedDates.length > 0) {
            markedDates.sort((a, b) => a - b);
            activityMap.set(activityName, markedDates);
            console.log(`${activityName}: ${formatLocalDate(markedDates[0])} - ${formatLocalDate(markedDates[markedDates.length - 1])}`);
          }
        }

        if (activityMap.size === 0) {
          continue;
        }

        const activities = [];
        let totalMarkedCases = 0;
        const activityData = [];

        for (const [name, markedDates] of activityMap.entries()) {
          const checkedCases = markedDates.length;
          totalMarkedCases += checkedCases;

          activityData.push({
            name,
            markedDates,
            checkedCases
          });
        }

        let totalPercent = 0;

        for (let i = 0; i < activityData.length; i++) {
          const activity = activityData[i];
          let percent;

          if (i === activityData.length - 1) {
            percent = Number((100 - totalPercent).toFixed(2));
          } else {
            percent =
              totalMarkedCases > 0
                ? Number(((activity.checkedCases * 100) / totalMarkedCases).toFixed(2))
                : 0;

            totalPercent += percent;
          }

          activities.push({
            name: activity.name,
            startAt: formatLocalDate(activity.markedDates[0]),
            endAt: formatLocalDate(
              activity.markedDates[activity.markedDates.length - 1]
            ),
            percent,
          });
        }

        updates.push({ standNumber, exhibitorName, activities });

      }

      if (updates.length === 0) {
        toast.error("No valid plans detected");
        return;
      }

      const payload = {
        eventId,
        updates,
      };

      const response = await axiosInstance.put("/activity/todo", payload);
      const { notFound = [] } = response.data;

      if (notFound.length > 0) {
        toast.error(
          `${notFound.length} stand(s) not found in system: ${notFound.join(", ")}`,
          { duration: 6000 }
        );
      }

      const imported = updates.length - notFound.length;
      if (imported > 0) {
        toast.success(`Imported ${imported} stand(s) successfully`);
      }
      fetchEventActivities();
      e.target.value = "";
    } catch (err) {
      console.error("Import error:", err);
      toast.error("Excel import failed");
    } finally {
      setUploading(false);
    }
  };

  const handleCopyRetroLink = async () => {
    const url = `${window.location.origin}/${eventId}/retro`;
    await navigator.clipboard.writeText(url);
    setLinkCopied(true);
    toast.success("Retro plan link copied");
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleDownloadCodes = async () => {
    try {
      const res = await axiosInstance.get(`/stand/export-codes/${eventId}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `retro-codes-${eventId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      toast.error("Failed to download codes");
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="mx-auto">

        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg shadow-blue-500/30">
                <CiMap className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900">
                  Retro Plan Import
                </h1>
                <p className="text-gray-600 text-xs">
                  Upload stand production schedules to import activities
                </p>
              </div>
            </div>
          </div>
        </div>
        {!isSubAdmin && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 sm:p-5 mb-6">
            <div className="flex items-center gap-2 mb-3.5">
              <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                <CiMap className="w-4 h-4 text-blue-900" />
              </div>
              <h2 className="text-sm font-semibold text-gray-800">Retro Plan Setup</h2>
            </div>

            <div className="flex flex-col sm:flex-row gap-2.5">
              <button
                onClick={() => handleDownloadTemplate(eventId)}
                className="group flex-1 inline-flex items-center justify-center gap-2 px-4 py-3
          bg-gradient-to-r from-emerald-600 to-emerald-500
          text-white text-sm font-semibold rounded-xl
          shadow-md shadow-emerald-600/20
          hover:shadow-lg hover:shadow-emerald-600/30
          hover:from-emerald-700 hover:to-emerald-600
          active:scale-[0.98] transition-all duration-200"
              >
                <FiDownload className="w-4 h-4 shrink-0 transition-transform group-hover:-translate-y-0.5" />
                <span className="truncate">Download Excel Template</span>
              </button>

              <button
                onClick={handleCopyRetroLink}
                className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold rounded-xl border transition-all duration-200 active:scale-[0.98] ${linkCopied
                  ? "bg-emerald-50 border-emerald-300 text-emerald-700"
                  : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
                  }`}
              >
                {linkCopied ? (
                  <>
                    <FiCheck className="w-4 h-4 shrink-0" /> Link Copied
                  </>
                ) : (
                  <>
                    <FiLink className="w-4 h-4 shrink-0" /> Copy Public Link
                  </>
                )}
              </button>

              <button
                onClick={handleDownloadCodes}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3
          bg-blue-950 text-white text-sm font-semibold rounded-xl
          shadow-md shadow-blue-950/20
          hover:bg-blue-900 hover:shadow-lg hover:shadow-blue-950/25
          active:scale-[0.98] transition-all duration-200"
              >
                <FiDownload className="w-4 h-4 shrink-0" />
                <span className="truncate">Download Stand Codes</span>
              </button>
            </div>

            <p className="text-xs text-gray-400 mt-3">
              Download the template for exhibitors filling schedules manually, or share the public link and codes so they
              can submit online instead.
            </p>
          </div>
        )}
        {!isSubAdmin && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden mb-6">
            <div className="p-6">
              <label className="block">
                <div className="flex items-center gap-2 mb-3">
                  <FiUpload className="w-5 h-5 text-gray-700" />
                  <span className="text-sm font-semibold text-gray-700">
                    Upload Excel Files
                  </span>
                </div>

                <div className="relative">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleExcelUpload}
                    disabled={uploading}
                    multiple
                    className="hidden"
                    id="file-upload"
                  />
                  <label
                    htmlFor="file-upload"
                    className={`
                    flex flex-col items-center justify-center
                    border-2 border-dashed rounded-xl p-8
                    transition-all duration-200 cursor-pointer
                    ${uploading
                        ? 'border-gray-300 bg-gray-50 cursor-not-allowed'
                        : 'border-blue-300 bg-blue-50 hover:bg-blue-100 hover:border-blue-400'
                      }
                  `}
                  >
                    <div className={`
                    p-4 rounded-full mb-4
                    ${uploading ? 'bg-gray-200' : 'bg-blue-100'}
                  `}>
                      <FiFile className={`w-8 h-8 ${uploading ? 'text-gray-400' : 'text-blue-600'}`} />
                    </div>
                    <p className={`text-sm font-medium mb-1 ${uploading ? 'text-gray-500' : 'text-gray-700'}`}>
                      {uploading ? 'Processing files...' : 'Click to browse or drag files here'}
                    </p>
                    <p className="text-xs text-gray-500">
                      Supports .xlsx and .xls files (multiple files allowed)
                    </p>
                  </label>
                </div>
              </label>
            </div>

            {uploading && (
              <div className="px-6 pb-6">
                <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent"></div>
                  <p className="text-sm font-medium text-blue-900">
                    Importing Excel files...
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {activities.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-600 rounded-lg">
                  <FiCheckCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Activities Loaded
                  </h2>
                  <p className="text-sm text-gray-600">
                    {activities.length} {activities.length === 1 ? 'activity' : 'activities'} imported for this event
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <FiActivity className="w-4 h-4" />
                <span>All activities are ready for production planning</span>
              </div>
            </div>
          </div>
        )}

        {activities.length === 0 && !uploading && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 text-center">
            <div className="inline-flex p-4 bg-gray-100 rounded-full mb-4">
              <FiActivity className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No Activities Yet
            </h3>
            <p className="text-sm text-gray-600">
              Upload Excel files to import activities for this event
            </p>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow border border-gray-200 p-4 mt-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">

            <input
              type="text"
              placeholder="Search exhibitor or stand..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm"
            />

            <select
              value={hallFilter}
              onChange={(e) => setHallFilter(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm"
            >
              <option value="all">All Halls</option>
              {[...new Set(stands.map(s => s.hall).filter(Boolean))].map(h => (
                <option key={h} value={h}>Hall {h}</option>
              ))}
            </select>

            <select
              value={tentFilter}
              onChange={(e) => setTentFilter(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm"
            >
              <option value="all">All Tents</option>
              {[...new Set(stands.map(s => s.tentNumber).filter(Boolean))].map(t => (
                <option key={t} value={t}>Tent {t}</option>
              ))}
            </select>

            <select
              value={retroFilter}
              onChange={(e) => setRetroFilter(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm"
            >
              <option value="all">All Retro Plans</option>
              <option value="yes">Has Retro Plan</option>
              <option value="no">No Retro Plan</option>
            </select>

          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Stands Overview
            </h2>
            <span className="text-sm text-gray-500">
              {filteredStands.length} / {stands.length} stands
            </span>
          </div>

          <div className="divide-y divide-gray-100 max-h-[450px] overflow-y-auto">
            <div className="bg-white shadow border border-gray-200 overflow-hidden">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                  <tr>
                    <th className="px-6 py-3 text-left">Status</th>
                    <th className="px-6 py-3 text-left">Company</th>
                    <th className="px-6 py-3 text-left">Stand</th>
                    <th className="px-6 py-3 text-left">Hall</th>
                    <th className="px-6 py-3 text-left">Tent</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-100">
                  {filteredStands.map((stand) => (
                    <tr
                      key={stand._id}
                      className="hover:bg-gray-50 transition"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span
                            className={`w-3 h-3 rounded-full inline-block ${!hasRetro ? "bg-blue-500" : stand.hasRetroPlan ? "bg-green-500" : "bg-orange-400"
                              }`}
                            title={stand.hasRetroPlan ? "Retro plan uploaded" : "Retro plan pending"}
                          />
                          <span
                            className={`text-xs font-medium ${!hasRetro ? "text-blue-700" : stand.hasRetroPlan ? "text-green-700" : "text-orange-600"
                              }`}
                          >
                            {!hasRetro ? "Auto" : stand.hasRetroPlan ? "Uploaded" : "Pending"}
                          </span>
                        </div>
                      </td>

                      <td className="px-6 py-4 font-medium text-gray-800">
                        {stand.exhibitorName || "Unnamed Company"}
                      </td>

                      <td className="px-6 py-4 text-gray-600">
                        {stand.standNumber || "-"}
                      </td>

                      <td className="px-6 py-4 text-gray-600">
                        {stand.hall || "-"}
                      </td>

                      <td className="px-6 py-4 text-gray-600">
                        {stand.tentNumber || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredStands.length === 0 && (
              <div className="p-8 text-center text-sm text-gray-500">
                No stands match your filters
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default RetroPlan;